import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchFactoryById, type Factory } from '@/core/api';

interface FactoryState {
  selectedFactoryId: string | null;
  isTransitioning: boolean;
  hasHydrated: boolean;
  setSelectedFactory: (id: string | null) => void;
  setTransitioning: (value: boolean) => void;
  setHydrated: (value: boolean) => void;
}

export const useFactoryStore = create<FactoryState>()(
  persist(
    (set) => ({
      selectedFactoryId: null,
      isTransitioning: false,
      hasHydrated: false,
      setSelectedFactory: (id) => set({ selectedFactoryId: id }),
      setTransitioning: (value) => set({ isTransitioning: value }),
      setHydrated: (value) => set({ hasHydrated: value }),
    }),
    { 
      name: 'selected-factory',
      partialize: (state) => ({ selectedFactoryId: state.selectedFactoryId }), // Don't persist isTransitioning
      onRehydrateStorage: () => (state) => {
        state?.setHydrated(true);
      },
    }
  )
);

/**
 * Factory-scoped query key prefixes that should be cleared on factory change.
 * When the user switches factories, all queries with these prefixes are removed
 * from the cache to prevent stale data from the previous factory.
 */
export const FACTORY_SCOPED_QUERY_PREFIXES = [
  // Core factory structure
  'factory-areas',
  'production-lines', 
  'stations',
  'factory-details',
  
  // User management
  'factory-users',
  'factory-roles',
  'roles-with-permissions',
  'user-locations',
  
  // Work instructions & checklists
  'work-instructions',
  'active-checklists',
  'checklist-details',
  'recent-submissions',
  'operator-pending-instructions',
  'operator-pending-alerts',
  
  // Data sources & mappings
  'data-sources',
  'field-mappings',
  'influx-buckets',
  'influx-measurements',
  'influx-fields',
  
  // Competency
  'competencies',
  'user-competencies',
  'operator-competency-stats',
  
  // 8D module
  'mod_8d_claims',
  'mod_8d_suppliers',
  
  // Suppliers & quality
  'suppliers',
  'incoming-inspections',
  'recent-inspection-results',
  
  // Documents
  'documents',
  
  // Energy
  'energy-values',
  'energy-config',
  
  // Articles & Customers
  'articles',
  'customers',
  
  // Scrap tracking
  'scrap-entries',
  'scrap-daily-summaries',
  
  // Customer complaints
  'customer-complaints',
  'complaint-stats',
  
  // LPA module
  'mod_lpa_questions',
  'mod_lpa_factory_settings',
  'mod_lpa_audits',
  'mod_lpa_audit',
  'mod_lpa_schedules',
  'mod_lpa_schedule',
  'mod_lpa_schedule_questions',
  'mod_lpa_instances',
  'mod_lpa_schedule_instances_by_schedule',
  'mod_lpa_instance',
];

/**
 * Hook for changing the selected factory with proper cache management.
 * Clears all factory-scoped queries from cache to prevent stale data.
 */
export const useFactoryChange = () => {
  const queryClient = useQueryClient();
  const { setSelectedFactory, setTransitioning } = useFactoryStore();

  const changeFactory = (newFactoryId: string | null) => {
    const currentFactoryId = useFactoryStore.getState().selectedFactoryId;
    
    // Skip if same factory
    if (currentFactoryId === newFactoryId) return;
    
    setTransitioning(true);
    
    // Remove all factory-scoped queries from cache
    // This ensures fresh data is fetched for the new factory
    queryClient.removeQueries({ 
      predicate: (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key)) return false;
        
        const prefix = key[0];
        if (typeof prefix !== 'string') return false;
        
        return FACTORY_SCOPED_QUERY_PREFIXES.includes(prefix);
      }
    });
    
    // Update the selected factory
    setSelectedFactory(newFactoryId);
    
    // Clear transitioning state after a short delay to allow re-renders
    setTimeout(() => setTransitioning(false), 150);
  };

  return { changeFactory };
};

/**
 * Main hook for accessing factory context.
 * Provides the selected factory ID, factory object, and change handler.
 * 
 * @example
 * const { selectedFactoryId, selectedFactory, setSelectedFactory, isLoading } = useFactory();
 */
export const useFactory = () => {
  const { selectedFactoryId, isTransitioning } = useFactoryStore();
  const { changeFactory } = useFactoryChange();

  const { data: selectedFactory, isLoading: isLoadingFactory } = useQuery({
    queryKey: ['selected-factory', selectedFactoryId],
    queryFn: () => fetchFactoryById(selectedFactoryId!),
    enabled: !!selectedFactoryId,
    staleTime: 5 * 60 * 1000, // Factory data is stable, cache for 5 minutes
  });

  return {
    selectedFactoryId,
    selectedFactory: selectedFactory as Factory | null,
    setSelectedFactory: changeFactory, // Use the enhanced change handler
    isLoading: isLoadingFactory || isTransitioning,
    isTransitioning,
  };
};
